import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Target, Trophy } from "lucide-react";
import type { HighlightsDto } from "@/types/api";
import { highlightsApi } from "@/api/highlights";
import { useAppContext } from "@/app/ContextProvider";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatMatchDate } from "@/lib/utils";

export function HighlightsBanner() {
  const { isReady, leagueId, tournamentId } = useAppContext();
  const [showAll, setShowAll] = useState(false);

  const q = useQuery({
    queryKey: ["highlights", leagueId, tournamentId],
    queryFn: () => highlightsApi.get({ leagueId: leagueId!, tournamentId: tournamentId! }),
    enabled: isReady,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const h: HighlightsDto | undefined = q.data;
  if (!h) return null;

  const hasBullseye = !!h.bullseyes && h.bullseyes.winnerNames.length > 0;
  const hasAny = !!h.dailyTop || !!h.weeklyTop || hasBullseye || h.allBullseyes.length > 0;
  if (!hasAny) return null;

  return (
    <>
      <div className="mb-4 flex gap-2 overflow-x-auto rounded-xl border border-border/60 bg-card/50 p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {h.dailyTop && (
          <Chip icon={<Trophy className="size-4 text-warning" />} label="A nap tippelője">
            {h.dailyTop.fullName} <span className="text-muted-foreground">({h.dailyTop.points} pont)</span>
          </Chip>
        )}
        {h.weeklyTop && (
          <Chip icon={<CalendarDays className="size-4 text-primary" />} label="A hét tippelője">
            {h.weeklyTop.fullName} <span className="text-muted-foreground">({h.weeklyTop.points} pont)</span>
          </Chip>
        )}
        {hasBullseye ? (
          <Chip
            icon={<Target className="size-4 text-success" />}
            label={`Legutóbbi telitalálat (${h.bullseyes!.teamAName} ${h.bullseyes!.scoreA}–${h.bullseyes!.scoreB} ${h.bullseyes!.teamBName})`}
            onClick={h.allBullseyes.length > 0 ? () => setShowAll(true) : undefined}
          >
            {h.bullseyes!.winnerNames.join(", ")}
          </Chip>
        ) : h.allBullseyes.length > 0 ? (
          <Chip icon={<Target className="size-4 text-success" />} label="Telitalálatos meccsek" onClick={() => setShowAll(true)}>
            {h.allBullseyes.length} db
          </Chip>
        ) : null}
      </div>

      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="size-5 text-success" /> Telitalálatos meccsek
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <ul className="space-y-2">
              {h.allBullseyes.map((m) => (
                <li key={m.matchId} className="rounded-lg border border-border/60 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">
                      {m.teamAName} <span className="tabular-nums">{m.scoreA}–{m.scoreB}</span> {m.teamBName}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatMatchDate(m.matchDate)}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{m.winnerNames.join(", ")}</div>
                </li>
              ))}
            </ul>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Chip({
  icon,
  label,
  children,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  const content = (
    <>
      {icon}
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium">{children}</span>
    </>
  );
  const cls = "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border/50 bg-background/40 px-3 py-1.5 text-sm";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${cls} transition-colors hover:border-primary/50`} title="Összes telitalálatos meccs">
        {content}
      </button>
    );
  }
  return <div className={cls}>{content}</div>;
}
